import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

const STATUS_ICON = {
  pending: <HourglassEmptyIcon fontSize="small" color="disabled" />,
  running: <CircularProgress size={18} />,
  ok: <CheckCircleIcon fontSize="small" color="success" />,
  error: <ErrorIcon fontSize="small" color="error" />,
  skipped: <SkipNextIcon fontSize="small" color="warning" />,
  warning: <WarningAmberIcon fontSize="small" color="warning" />,
};

const STATUS_CHIP_COLOR = {
  pending: "default",
  running: "info",
  ok: "success",
  error: "error",
  skipped: "warning",
  warning: "warning",
};

function formatElapsedMs(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n) || n < 0) return "—";
  if (n < 1000) return `${Math.round(n)} ms`;
  if (n < 60000) return `${(n / 1000).toFixed(1)} s`;
  const mins = Math.floor(n / 60000);
  const secs = Math.floor((n % 60000) / 1000);
  return `${mins}m ${secs}s`;
}

export function mapServerProgress(serverSteps = [], labelByStep = {}) {
  return (serverSteps || []).map((step) => ({
    id: step.step,
    label: labelByStep[step.step] || step.step,
    status: step.status || "ok",
    detail: step.detail || null,
    durationMs: step.duration_ms,
  }));
}

export function useOperationProgress(stepDefs, running) {
  const [steps, setSteps] = useState([]);

  useEffect(() => {
    if (!running) return undefined;
    setSteps(
      stepDefs.map((def, index) => ({
        ...def,
        status: index === 0 ? "running" : "pending",
        startedAt: index === 0 ? Date.now() : null,
      }))
    );
    return undefined;
  }, [running, stepDefs]);

  const applyServerProgress = (serverSteps, labelByStep = {}) => {
    if (!serverSteps?.length) return;
    setSteps(mapServerProgress(serverSteps, labelByStep));
  };

  return { steps, setSteps, applyServerProgress };
}

export default function ConsoleOperationSteps({
  running = false,
  steps = [],
  summaryLabel = "",
  activityLabel = "",
  showPercent = true,
  operationStartedAt = null,
  maxHeight = 280,
  onStatClick = null,
  selectedStat = null,
  /** Quando true, exibe só barra de progresso/atividade (sem lista de etapas). */
  hideStepList = false,
}) {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!running) return undefined;
    const id = window.setInterval(() => setTick((n) => n + 1), 500);
    return () => window.clearInterval(id);
  }, [running]);

  const summary = useMemo(() => {
    if (!steps.length) return null;
    const done = steps.filter((s) => ["ok", "error", "skipped", "warning"].includes(s.status)).length;
    const ok = steps.filter((s) => s.status === "ok").length;
    const failed = steps.filter((s) => s.status === "error").length;
    const runningCount = steps.filter((s) => s.status === "running").length;
    return { done, ok, failed, running: runningCount, total: steps.length };
  }, [steps]);

  const elapsedLabel = useMemo(() => {
    if (!operationStartedAt) return null;
    void tick;
    return formatElapsedMs(Date.now() - operationStartedAt);
  }, [operationStartedAt, tick]);

  const stepElapsed = (step) => {
    if (step.durationMs != null) return formatElapsedMs(step.durationMs);
    if (step.status === "running" && step.startedAt) {
      void tick;
      return formatElapsedMs(Date.now() - step.startedAt);
    }
    return null;
  };

  if (!running && !steps.length) return null;

  const pct = summary?.total ? (summary.done / summary.total) * 100 : 0;

  return (
    <Box sx={{ mb: 2 }}>
      {summary && (running || summary.done > 0) ? (
        <Box sx={{ mb: 1.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }} useFlexGap flexWrap="wrap">
            <Typography variant="body2" color="text.secondary">
              {summaryLabel}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              {elapsedLabel ? (
                <Typography variant="caption" color="text.secondary">
                  {elapsedLabel}
                </Typography>
              ) : null}
              {showPercent ? (
                <Typography variant="caption" color="text.secondary">
                  {Math.round(pct)}%
                </Typography>
              ) : null}
            </Stack>
          </Stack>
          <LinearProgress
            variant={running && summary.done < summary.total ? "indeterminate" : "determinate"}
            value={pct}
            sx={{ mb: 0.75 }}
          />
          {activityLabel ? (
            <Typography variant="caption" color="text.secondary" display="block">
              {activityLabel}
            </Typography>
          ) : null}
        </Box>
      ) : running ? (
        <>
          <LinearProgress sx={{ mb: 0.75 }} />
          {activityLabel ? (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              {activityLabel}
            </Typography>
          ) : null}
        </>
      ) : null}

      {!hideStepList && steps.length ? (
        <Paper variant="outlined" sx={{ maxHeight, overflow: "auto" }}>
          <List dense disablePadding>
            {steps.map((step) => {
              const elapsed = stepElapsed(step);
              return (
                <ListItem
                  key={step.id}
                  alignItems="flex-start"
                  sx={{
                    py: 0.75,
                    borderBottom: 1,
                    borderColor: "divider",
                    "&:last-child": { borderBottom: 0 },
                    flexDirection: "column",
                    alignItems: "stretch",
                  }}
                >
                  <Box sx={{ display: "flex", width: "100%" }}>
                    <ListItemIcon sx={{ minWidth: 36, mt: 0.25 }}>
                      {STATUS_ICON[step.status] || STATUS_ICON.pending}
                    </ListItemIcon>
                    <ListItemText
                      sx={{ m: 0, flex: 1 }}
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" useFlexGap flexWrap="wrap">
                          <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                            {step.label}
                          </Typography>
                          <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
                            <Chip
                              size="small"
                              label={step.status}
                              color={STATUS_CHIP_COLOR[step.status] || "default"}
                              variant={step.status === "pending" ? "outlined" : "filled"}
                            />
                            {elapsed ? (
                              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 52, textAlign: "right" }}>
                                {elapsed}
                              </Typography>
                            ) : null}
                          </Stack>
                        </Stack>
                      }
                      secondary={
                        step.detail ? (
                          <Typography
                            variant="caption"
                            color={step.status === "error" ? "error" : "text.secondary"}
                            component="span"
                            display="block"
                          >
                            {step.detail}
                          </Typography>
                        ) : null
                      }
                      secondaryTypographyProps={{ component: "div" }}
                    />
                  </Box>
                  {step.stats?.length ? (
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.75, pl: 4.5 }}>
                      {step.stats.map((stat) => {
                        const clickable = Boolean(onStatClick && stat.clickable !== false);
                        const selected =
                          selectedStat?.stepId === step.id && selectedStat?.statKey === stat.key;
                        return (
                          <Chip
                            key={stat.key}
                            size="small"
                            label={`${stat.label}: ${stat.value}`}
                            color={stat.color || "default"}
                            variant={
                              selected ? "filled" : stat.variant || (stat.color ? "filled" : "outlined")
                            }
                            onClick={
                              clickable
                                ? (e) => {
                                    e.stopPropagation();
                                    onStatClick(step, stat);
                                  }
                                : undefined
                            }
                            sx={{
                              height: 22,
                              "& .MuiChip-label": { px: 1, fontSize: "0.7rem" },
                              ...(clickable
                                ? {
                                    cursor: "pointer",
                                    ...(selected
                                      ? { boxShadow: 2, outline: "2px solid", outlineColor: "primary.main" }
                                      : { "&:hover": { opacity: 0.88 } }),
                                  }
                                : {}),
                            }}
                          />
                        );
                      })}
                    </Stack>
                  ) : null}
                </ListItem>
              );
            })}
          </List>
        </Paper>
      ) : null}
    </Box>
  );
}
